/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import { schema } from 'normalizr';

import { getResponseText, removeVariables } from '../../util/actionUtil';

export const stepSchema = new schema.Entity(
  'steps',
  {},
  {
    idAttribute: 'step', // referring to actionId
    processStrategy: step => {
      let { title } = step;
      const responseText = getResponseText(step) || 'No response';
      const responseTextMaskedVariable = removeVariables(responseText);
      if (!title) {
        title = responseTextMaskedVariable;
      }
      return {
        ...step,
        id: step.step,
        responseText,
        variableTitle: title
      };
    }
  }
);

export const subActionSchema = new schema.Entity(
  'sub_actions',
  {},
  {
    idAttribute: 'action',
    processStrategy: subAction => {
      const updatedSubAction = { ...subAction };
      const { steps, variables } = updatedSubAction;
      if (steps) {
        const updatedSteps = [];
        steps.forEach(step => {
          //this schema is used for two separate payloads
          //in one case this schema is used to normalize the sub_action_references which are returned
          //when we fetch a single action.
          //in the other case, this schema is used when we fetch an action which we already know
          //is being used as a sub action.
          const dataType = step.data_type || step.question?.data_type;
          const entity = step.entity || _.get(step, 'question.entity');
          const freeText = step.free_text || _.get(step, 'question.free_text');
          const updatedStep = { ...step };
          updatedStep.data_type = dataType;
          updatedStep.entity = entity;
          updatedStep.free_text = freeText;
          if (!step.title) {
            updatedStep.title = 'Untitled';
            const { variable } = step;
            const stepVariable = _.find(variables, { variable });
            if (stepVariable && stepVariable.title) {
              updatedStep.title = stepVariable.title;
            }
          }
          updatedStep.subActionId = updatedSubAction.action;
          updatedSteps.push(updatedStep);
        });
        updatedSubAction.steps = updatedSteps;
        updatedSubAction.id = updatedSubAction.action;
      }
      return updatedSubAction;
    }
  }
);

export const actionVariableSchema = new schema.Entity(
  'action_variables',
  {},
  {
    idAttribute: 'variable'
  }
);

export const actionSchema = new schema.Entity(
  'actions',
  {
    steps: [stepSchema],
    sub_action_references: [subActionSchema],
    variables: [actionVariableSchema]
  },
  {
    idAttribute: 'action', // referring to actionId
    processStrategy: action => ({ ...action, id: action.action })
  } //add an 'id' prop so that the datatable can correctly just list these
);

export const exampleSchema = new schema.Entity(
  'examples',
  {},
  { idAttribute: 'text' }
);

export const intentSchema = new schema.Entity(
  'intents',
  {
    examples: [exampleSchema]
  },
  { idAttribute: 'intent' }
);

const counterExampleSchema = new schema.Entity(
  'counterExamples',
  {},
  {
    idAttribute: 'text'
  }
);

export const counterExamplesSchema = new schema.Array(counterExampleSchema);

export const actionsSchema = new schema.Array(actionSchema);
